import { useEffect, useRef } from "react";
import { observer } from "mobx-react-lite";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ArrowLeft, Play, Square, RotateCcw, LayoutDashboard, Terminal, ScrollText,
  Folder, Package, Boxes, Users, Megaphone, Settings, Loader2, Copy, Plug, BarChart3, Save, Wrench, Zap,
  Map as MapIcon,
} from "lucide-react";
import { apiFetch } from "../../utils/api";
import { serverStore } from "../../stores/server.store";
import { userStore } from "../../stores/user.store";
import { ServerIcon } from "../server/ServerIcon";
import { WorkspaceNav, type NavItem } from "./WorkspaceNav";
import { Badge } from "../design/Badge";
import { EmptyState } from "../design/EmptyState";
import { cn } from "../../utils/cn";

import { ServerDashboard } from "../tabs/ServerDashboard";
import { ServerConsole } from "../tabs/ServerConsole";
import { ServerLogsTab } from "../tabs/ServerLogsTab";
import FileManager from "../server/FileManager";
import VanillaTab from "../tabs/VanillaTab";
import BedrockTab from "../tabs/BedrockTab";
import SteamTab from "../tabs/SteamTab";
import { ModpacksTab } from "../tabs/ModpacksTab";
import { OptimizeTab } from "../tabs/OptimizeTab";
import { PlayerList } from "../server/PlayerList";
import { OfflinePlayersPanel } from "../server/KnownPlayers";
import { MapTab } from "../tabs/MapTab";
import { PlayerStats } from "../server/PlayerStats";
import { AnnouncementsTab } from "../tabs/AnnouncementsTab";
import { ServerSettingsCard } from "../server/ServerSettingsCard";
import { CloudBackupTab } from "../tabs/CloudBackupTab";
import { can as canUser } from '@/utils/permissions';

const PowerBtn = ({ children, title, onClick, danger, accent }: any) => (
  <button
    title={title}
    aria-label={title}
    onClick={onClick}
    className={cn(
      "w-9 h-9 rounded-lg flex items-center justify-center transition-colors border",
      accent ? "bg-ember/15 border-ember/25 text-ember hover:bg-ember/25"
        : danger ? "border-line text-fg-muted hover:text-red-400 hover:border-red-500/30 hover:bg-red-500/10"
        : "border-line text-fg-muted hover:text-fg hover:bg-white/[0.06]"
    )}
  >
    {children}
  </button>
);

export const ServerWorkspace = observer(() => {
  const { serverId, section = "overview" } = useParams();
  const navigate = useNavigate();
  const cu = userStore.currentUser;

  useEffect(() => {
    if (serverId && serverStore.selectedServerId !== serverId) serverStore.selectedServerId = serverId;
  }, [serverId]);

  // Jede Sektion oben beginnen - der Inhaltsbereich behielt sonst die Scrollposition
  // der vorigen Sektion (Einstellungen öffneten mitten auf der Seite).
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 });
  }, [serverId, section]);

  const server = serverStore.servers.find((s) => s.id === serverId);
  if (!server) {
    return (
      <div className="p-8">
        <EmptyState icon={<Loader2 size={20} className="animate-spin" />} title="Server wird geladen…" />
      </div>
    );
  }

  const online = server.status === "online";
  const busy = ["restarting", "stopping", "starting"].includes(server.status);
  const can = (p: string) => canUser(cu, p);
  const power = (status: "online" | "offline" | "restarting") => serverStore.powerServer(server.id, status);
  const isSteam = server.type?.startsWith("steam");
  const isJava = !isSteam && server.type !== "bedrock";

  const sections: NavItem[] = [
    { id: "overview", label: "Übersicht", icon: <LayoutDashboard size={16} /> },
    // Betrieb
    ...(can("can_view_console") ? [
      { id: "console", label: "Konsole", icon: <Terminal size={16} />, group: "Betrieb" },
      { id: "logs", label: "Logs", icon: <ScrollText size={16} />, group: "Betrieb" },
    ] : []),
    ...(can("can_manage_files") ? [{ id: "files", label: "Dateien", icon: <Folder size={16} />, group: "Betrieb" }] : []),
    // Spiel & Inhalte
    { id: "software", label: isSteam ? "Spiel & Mods" : "Software", icon: <Package size={16} />, group: "Spiel & Inhalte" },
    ...(isJava ? [{ id: "modpacks", label: "Modpacks", icon: <Boxes size={16} />, group: "Spiel & Inhalte" }] : []),
    ...(isJava ? [{ id: "optimize", label: "Optimieren", icon: <Zap size={16} />, group: "Spiel & Inhalte" }] : []),
    // Community
    ...(!isSteam ? [{ id: "players", label: "Spieler", icon: <Users size={16} />, group: "Community" }] : []),
    ...(isJava ? [{ id: "map", label: "Karte", icon: <MapIcon size={16} />, group: "Community" }] : []),
    ...(!isSteam && can("can_view_player_stats") ? [{ id: "stats", label: "Statistik", icon: <BarChart3 size={16} />, group: "Community" }] : []),
    ...(can("can_manage_schedules") ? [{ id: "announcements", label: "Ankündigungen", icon: <Megaphone size={16} />, group: "Community" }] : []),
    // Verwaltung
    ...(can("can_manage_backups") ? [{ id: "backups", label: "Backups", icon: <Save size={16} />, group: "Verwaltung" }] : []),
    { id: "settings", label: "Einstellungen", icon: <Settings size={16} />, group: "Verwaltung" },
  ];

  const renderSection = () => {
    switch (section) {
      case "console": return <ServerConsole />;
      case "logs": return <ServerLogsTab />;
      case "files": return <FileManager serverId={server.id} />;
      case "software":
        if (isSteam) return <SteamTab />;
        if (server.type === "bedrock") return <BedrockTab />;
        return <VanillaTab />;
      case "modpacks": return <ModpacksTab />;
      case "optimize": return <OptimizeTab />;
      case "players":
        // Offline: Live-Liste geht nicht, aber Bekannte/Gebannte verwalten
        // schon (Ban/Unban editiert dann direkt banned-players.json).
        return online ? <PlayerList /> : <OfflinePlayersPanel serverId={server.id} />;
      case "map":
        return <MapTab />;
      case "stats":
        // Doppelt abgesichert: ohne Recht kein Zugriff (Backend gibt sonst 403).
        return can("can_view_player_stats") ? <PlayerStats /> : (
          <EmptyState icon={<BarChart3 size={20} />} title="Kein Zugriff" description="Dir fehlt die Berechtigung, Spieler-Statistiken einzusehen." />
        );
      case "announcements": return <AnnouncementsTab currentUser={cu} />;
      case "backups": return <CloudBackupTab currentUser={cu} />;
      case "settings": return <ServerSettingsCard key={server.id} server={server} />;
      default: return <ServerDashboard />;
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-5 md:px-8 pt-5 pb-4 border-b border-line bg-surface/40">
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={() => navigate("/")} className="p-1.5 -ml-1.5 rounded-lg text-fg-muted hover:text-fg hover:bg-white/[0.06] transition-colors" aria-label="Zurück zum Board">
            <ArrowLeft size={18} />
          </button>
          <ServerIcon serverId={server.id} type={server.type} size={34} className="rounded-lg" />
          <div className="min-w-0">
            <div className="flex items-center gap-2.5">
              <h1 className="font-display text-xl font-semibold text-fg tracking-tight truncate">{server.name}</h1>
              <Badge variant={online ? "success" : busy ? "warning" : "neutral"} size="sm">
                <span className={cn("w-1.5 h-1.5 rounded-full mr-1.5", online ? "bg-online gm-live-dot" : busy ? "bg-warn" : "bg-fg-muted")} />
                {online ? "läuft" : busy ? "…" : "gestoppt"}
              </Badge>
            </div>
            <div className="flex items-center gap-2 text-xs text-fg-muted font-mono mt-0.5 flex-wrap">
              <span>{server.type}{server.version ? ` · ${server.version}` : ""} · :{server.port}</span>
              <button
                onClick={() => navigator.clipboard?.writeText(server.id).then(() => toast.success("Server-ID kopiert"))}
                title="Server-ID kopieren"
                className="inline-flex items-center gap-1 text-fg-muted/70 hover:text-ember transition-colors"
              >
                <span className="truncate max-w-[160px]">{server.id}</span>
                <Copy size={11} className="shrink-0" />
              </button>
            </div>
          </div>

          {can("can_power_server") && (
            <div className="flex items-center gap-1.5 ml-auto">
              {!online && !busy && (
                <PowerBtn
                  title="Laufenden Prozess suchen & verbinden"
                  onClick={async () => {
                    const res = await apiFetch(`/api/servers/${server.id}/reconnect`, { method: "POST" });
                    const d = await res.json().catch(() => ({}));
                    if (d.reconnected) { toast.success(d.message); serverStore.fetchData(); }
                    else if (d.success) toast(d.message);
                    else toast.error(d.message || "Reconnect fehlgeschlagen");
                  }}
                >
                  <Plug size={16} />
                </PowerBtn>
              )}
              {!online && !busy && <PowerBtn title="Starten" accent onClick={() => power("online")}><Play size={16} /></PowerBtn>}
              {online && <PowerBtn title="Neustart" onClick={() => power("restarting")}><RotateCcw size={16} /></PowerBtn>}
              {online && <PowerBtn title="Stoppen" danger onClick={() => power("offline")}><Square size={16} /></PowerBtn>}
            </div>
          )}
        </div>

        {/* Auto-Update fehlgeschlagen / Rollback — sichtbar bis verworfen oder zum nächsten Erfolg */}
        {server.auto_update_config?.last_result && server.auto_update_config.last_result.status !== "success" && (
          <div className={cn(
            "mt-3 flex items-center gap-3 rounded-lg px-3 py-2 border",
            server.auto_update_config.last_result.status === "rolled_back"
              ? "bg-warn/[0.08] border-warn/25"
              : "bg-red-500/[0.08] border-red-500/25"
          )}>
            <span className={cn("text-xs shrink-0 font-medium", server.auto_update_config.last_result.status === "rolled_back" ? "text-warn" : "text-red-400")}>
              {server.auto_update_config.last_result.status === "rolled_back" ? "↩ Auto-Update zurückgerollt" : "⚠ Auto-Update fehlgeschlagen"}
            </span>
            <span className="text-xs text-fg-muted truncate flex-1">
              {server.auto_update_config.last_result.label}
              {server.auto_update_config.last_result.error ? ` — ${server.auto_update_config.last_result.error}` : ""}
            </span>
            <button
              onClick={() => serverStore.handleSaveServer({ id: server.id, auto_update_config: { ...server.auto_update_config, last_result: null } })}
              className="text-fg-muted hover:text-fg shrink-0 text-xs underline-offset-2 hover:underline"
            >
              Verwerfen
            </button>
          </div>
        )}

        {/* Laufende Operation — sichtbar in jeder Sektion. Selbstheilung wird
            deutlich hervorgehoben, damit niemand waehrenddessen eingreift. */}
        {(() => {
          const act = serverStore.getServerActivity(server.id);
          if (!act) return null;
          const heal = act.kind === "heal";
          return (
            <div className={cn(
              "mt-3 flex items-center gap-3 rounded-lg px-3 py-2 border",
              heal ? "bg-amber-500/[0.1] border-amber-500/30" : "bg-ember/[0.07] border-ember/20"
            )}>
              {heal
                ? <Wrench size={14} className="text-amber-400 shrink-0 animate-pulse" />
                : <Loader2 size={14} className="text-ember animate-spin shrink-0" />}
              <div className="min-w-0 flex-1">
                {heal && <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-400 leading-tight">Selbstheilung läuft — bitte nicht eingreifen</div>}
                <span className="text-xs text-fg truncate block">{act.label}</span>
              </div>
              {act.progress !== null && <span className={cn("text-xs font-mono shrink-0", heal ? "text-amber-400" : "text-ember")}>{act.progress}%</span>}
              <div className="w-28 h-1 rounded-full bg-white/[0.08] overflow-hidden shrink-0 hidden sm:block">
                <div className={cn("h-full transition-all duration-500", heal ? "bg-amber-400" : "bg-ember")} style={{ width: `${act.progress ?? 100}%` }} />
              </div>
            </div>
          );
        })()}

      </div>

      {/* Nav (links, gruppiert) + Content — auf Mobil gestapelt */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0 overflow-hidden">
        <WorkspaceNav items={sections} active={section} onChange={(id) => navigate(`/server/${server.id}/${id}`)} />
        <div ref={contentRef} className="flex-1 overflow-y-auto p-5 md:p-8 min-w-0">
          {renderSection()}
        </div>
      </div>
    </div>
  );
});
